import { useEffect, useRef } from "react";
import { Col, Row } from "reactstrap";
import { Form, Formik } from "formik";
import { InputField } from "../../../../../Component/Forms/FormsControl/CustomInput/CustomInput";
import NewCustomFormikDatePicker from "../../../../../Component/Forms/FormsControl/NewFormikDatePicker";
import { CustomSelectInput } from "../../../../../Component/Forms/FormsControl/CustomSelectInput/CustomSelectInput";
import { Btn, P } from "../../../../../AbstractElements";
import QuestionField from "../QuestionField";
import { Cancel, Save } from "../../../../../utils/Constant";
import { employeeUpdateProbation } from "../../redux/slice/EmployeeUpdateProbationSlice";
import { submitSurveyFormResponse } from "../../../../../ReduxToolkit/Slice/SurveyFormsSlice/SurveyFormResponseSubmit";
import { formattedYMDDate } from "../../../../../utils/dateUtils";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../ReduxToolkit/Hooks";
import { userProbationSchema } from "../../probationSchema";
import { IEmployeeUnderProbationList } from "../../../../../Api/Services/HRService";

type Question = {
  options: string[];
  question_label: string;
  is_required: number;
  question_type: number;
  question_type_name: string;
  form_id: number;
  question_response: string | string[];
  question_id: number;
};

type FormValues = Record<string, any>;

export const mergeQuestionResponses = (
  questions: Question[],
  formValues: FormValues
): Array<{ question_no: number; question_type: number; response: string }> => {
  return questions.map((q) => {
    const responseKey = `question_${q.question_id}`;
    let responseValue = formValues[responseKey];

    // Convert multi-select (type 5) to underscore-separated string
    if (q.question_type === 5 && Array.isArray(responseValue)) {
      responseValue = responseValue.map((r: any) => r.value).join("_");
    }

    //  If question type is date (type 6), format using formattedYMDDate
    if (q.question_type === 6 && responseValue) {
      try {
        responseValue = formattedYMDDate(responseValue);
      } catch (err) {
        console.warn("Invalid date format for question:", q.question_id, err);
      }
    }

    return {
      question_no: q.question_id,
      question_type: q.question_type,
      response: responseValue,
    };
  });
};

export interface IEmployeeProbationFormProps {
  onClose: () => void;
  setSelectedUser: any;
  selectedUser: IEmployeeUnderProbationList;
}

const EmployeeProbationForm = ({
  onClose,
  selectedUser,
  setSelectedUser,
}: IEmployeeProbationFormProps) => {
  const formikresetForm = useRef<any>(null);
  const dispatch = useAppDispatch();
  const { isOpen } = useAppSelector(
    ({ employeeProfileDetailModalSlice }) => employeeProfileDetailModalSlice
  );

  const { data: surveyFormQuestionDetails } = useAppSelector(
    ({ employeeSurveyFormResponse }) => employeeSurveyFormResponse
  );

  const userProbationQuestionForm = surveyFormQuestionDetails?.final_data?.map(
    (d) => ({
      ...d,
      options: d.options ? String(d.options).split("_") : [],
    })
  );

  const { data: centersList } = useAppSelector(({ centerList }) => centerList);
  const { data: departmentList } = useAppSelector(
    ({ departmentList }) => departmentList
  );
  const { data: userList } = useAppSelector(
    ({ employeesList }) => employeesList
  );

  const formName = surveyFormQuestionDetails?.form_name;

  // Initial values
  const questionInitialValues = userProbationQuestionForm?.reduce((acc, q) => {
    const key = `question_${q.question_id}`;

    if (q.question_type === 3) {
      // Checkbox type -> ensure it's an array
      acc[key] = Array.isArray(q.question_response)
        ? q.question_response
        : typeof q.question_response === "string" && q.question_response
        ? q.question_response.split("_").map((s) => s.trim())
        : [];
    } else if (q.question_type === 5) {
      // Multi-select → store as array of {label, value}
      acc[key] = q.question_response
        ? String(q.question_response)
            .split("_")
            .map((s) => ({ label: s, value: s }))
        : [];
    } else {
      // Text/Radio type -> ensure it's a string
      acc[key] = q.question_response ?? "";
    }

    return acc;
  }, {} as Record<string, any>);

  const initialValues = {
    name: selectedUser?.name || "",
    email: selectedUser?.email || "",
    department: selectedUser?.department_name || "",
    dateOfJoining: selectedUser?.doj || "",
    probationDate: selectedUser?.probation_date || "",
    extendedDate: selectedUser?.extended_date ? selectedUser.extended_date : "",
    probationStatus: selectedUser?.probation_status || "",
    probationRemark: selectedUser?.probation_remark || "",
    ...(surveyFormQuestionDetails ? questionInitialValues : {}),
  };

  const handleSubmit = async (values: any) => {
    const probationPayload = {
      user_id: Number(selectedUser?.user_id),
      probation_remark: values.probationRemark,
      probation_status: values.probationStatus,
      extended_date: values.extendedDate
        ? formattedYMDDate(values?.extendedDate ? values.extendedDate : "")
        : null,
    };

    if (values.probationStatus === "completed" && selectedUser?.form_id) {
      const questionFormResponse = mergeQuestionResponses(
        userProbationQuestionForm as Question[],
        values
      ); 
      if (questionFormResponse && questionFormResponse.length > 0) {
        await dispatch(
          submitSurveyFormResponse({
            form_id: Number(selectedUser.form_id),
            user_id: Number(selectedUser.user_id),
            payload: questionFormResponse,
          })
        );
      }
    }

    await dispatch(employeeUpdateProbation(probationPayload));
  };

  useEffect(() => {
    if (!isOpen && formikresetForm.current) {
      setSelectedUser(null);
      formikresetForm.current(); // Reset Formik when modal closes
    }
  }, [isOpen]);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={userProbationSchema(
        userProbationQuestionForm,
        surveyFormQuestionDetails
      )}
      enableReinitialize
    >
      {({ values, errors, touched, resetForm }) => {
        const probationStatus = values.probationStatus
          ?.toLowerCase()
          ?.replaceAll(" ", "");

        formikresetForm.current = resetForm;

        return (
          <Form className="theme-form">
            <div>
              <Row>
                <InputField
                  name="name"
                  label="Name"
                  errors={errors}
                  md="3"
                  type="text"
                  disabled={true}
                />
                <InputField
                  name="email"
                  label="Email"
                  errors={errors}
                  md="3"
                  type="text"
                  disabled={true}
                />
                <InputField
                  name="department"
                  label="Department"
                  errors={errors}
                  md="3"
                  type="text"
                  disabled={true}
                />
                <Col md="3">
                  <NewCustomFormikDatePicker
                    label="Date Of Joining"
                    name="dateOfJoining"
                    placeholder="Select Date"
                    disabled={true}
                  />
                </Col>
                <Col md="3">
                  <NewCustomFormikDatePicker
                    label="Probation End Date"
                    name="probationDate"
                    placeholder="Select Date"
                    disabled={true}
                  />
                </Col>
                <Col md="3">
                  <CustomSelectInput
                    label="Probation Status"
                    name="probationStatus"
                    type="select"
                    fieldClassName={`form-control pointer ${
                      errors?.probationStatus && touched?.probationStatus
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value="completed">Completed</option>
                    <option value="extended">Extended</option>
                  </CustomSelectInput>
                </Col>

                {probationStatus === "extended" && (
                  <Col md="3">
                    <NewCustomFormikDatePicker
                      label="Extended Date"
                      name="extendedDate"
                      placeholder="Select Date"
                    />
                  </Col>
                )}
                <InputField
                  as="textarea"
                  label="Remark"
                  name="probationRemark"
                  errors={errors}
                  touched={touched}
                  md="12"
                  rows="2"
                />
              </Row>
              {probationStatus === "completed" && selectedUser?.form_id && (
                <div className="probation-form-container">
                  <hr className="mt-2 mb-4" />
                  <P>{formName}</P>
                  <div className="question-form-container">
                    {(() => {
                      let globalIndex = 0;
                      return surveyFormQuestionDetails?.sections?.map(
                        (section, sIdx) => (
                          <div key={sIdx} className="mb-4">
                            {section.section_name && (
                              <h5 className="fw-bold">
                                {section.section_name}
                              </h5>
                            )}
                            <Row>
                              {section.questions.map((q) => {
                                globalIndex++;
                                const normalizedOptions = q.options
                                  ? String(q.options).split("_")
                                  : [];
                                return (
                                  <QuestionField
                                    key={q.question_id}
                                    index={globalIndex}
                                    question={{
                                      ...q,
                                      options: normalizedOptions,
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    userListData={userList?.data}
                                    departmentList={departmentList?.data}
                                    centersList={centersList?.data}
                                  />
                                );
                              })}
                            </Row>
                          </div>
                        )
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            <Row className="d-flex justify-content-end m-auto">
              <Col sm="3" md="2">
                <Btn
                  color="secondary"
                  className="f-12 d-flex justify-self-end w-100 justify-content-center"
                  onClick={onClose}
                >
                  {Cancel}
                </Btn>
              </Col>
              <Col sm="3" md="2">
                <Btn
                  className="f-12 d-flex justify-self-end w-100 justify-content-center"
                  color="primary"
                  type="submit"
                >
                  {Save}
                </Btn>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EmployeeProbationForm;
